import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { IClienteVendedorVeles } from '../../../../../../application/models'

interface IVisualizacaoClientesAccordionProps {
  idsClientes: number[]
  buscaDisponiveis: string
  buscaSelecionados: string
  clientesVendedorVeles: IClienteVendedorVeles[]
  canEdit: boolean
  onAddCliente: (idCliente: number) => void
  onRemoveCliente: (idCliente: number) => void
  onLimparTodos: () => void
  onBuscaDisponiveisChange: (value: string) => void
  onBuscaSelecionadosChange: (value: string) => void
}

export const VisualizacaoClientesAccordion = ({
  idsClientes,
  buscaDisponiveis,
  buscaSelecionados,
  clientesVendedorVeles,
  canEdit,
  onAddCliente,
  onRemoveCliente,
  onLimparTodos,
  onBuscaDisponiveisChange,
  onBuscaSelecionadosChange,
}: IVisualizacaoClientesAccordionProps) => {
  const termoDisponiveis = buscaDisponiveis.trim().toLowerCase()
  const clientesDisponiveis = clientesVendedorVeles.filter(c => !idsClientes.includes(c.IdCliente))
  const clientesDisponiveisFiltrados = termoDisponiveis
    ? clientesDisponiveis.filter(c =>
        (c.NomeFantasia && c.NomeFantasia.toLowerCase().includes(termoDisponiveis)) ||
        (c.RazaoSocial && c.RazaoSocial.toLowerCase().includes(termoDisponiveis)) ||
        (c.LoginCliente && c.LoginCliente.toLowerCase().includes(termoDisponiveis)) ||
        (c.CNPJ && c.CNPJ.toLowerCase().includes(termoDisponiveis))
      )
    : clientesDisponiveis

  const termoSelecionados = buscaSelecionados.trim().toLowerCase()
  const clientesSelecionados = clientesVendedorVeles.filter(c => idsClientes.includes(c.IdCliente))
  const clientesSelecionadosFiltrados = termoSelecionados
    ? clientesSelecionados.filter(c =>
        (c.NomeFantasia && c.NomeFantasia.toLowerCase().includes(termoSelecionados)) ||
        (c.RazaoSocial && c.RazaoSocial.toLowerCase().includes(termoSelecionados)) ||
        (c.LoginCliente && c.LoginCliente.toLowerCase().includes(termoSelecionados)) ||
        (c.CNPJ && c.CNPJ.toLowerCase().includes(termoSelecionados))
      )
    : clientesSelecionados

  return (
    <Accordion
      variant="outlined"
      defaultExpanded={false}
      sx={{
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        overflow: 'hidden'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'action.hover',
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            my: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
            flexWrap: 'wrap',
            gap: 1
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Visualizacao de Clientes VELES
            </Typography>
            <Chip
              size="small"
              color={idsClientes.length > 0 ? 'primary' : 'default'}
              label={`${idsClientes.length} selecionado(s)`}
              sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Selecione os clientes do Vendedor Veles que este usuario podera visualizar.
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        <Grid container spacing={2}>

          {/* Coluna 1: Disponiveis para Selecao */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                height: 380,
                overflow: 'hidden'
              }}
            >
              {/* Cabecalho Disponiveis */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Disponiveis para Selecao
                </Typography>
                <Chip
                  size="small"
                  label={`${clientesDisponiveis.length}`}
                  sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
                />
              </Box>

              {/* Barra de Pesquisa dos Disponiveis */}
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Pesquisar disponiveis..."
                  value={buscaDisponiveis}
                  onChange={(e) => onBuscaDisponiveisChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: buscaDisponiveis ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => onBuscaDisponiveisChange('')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Box>

              {/* Lista de Clientes Disponiveis */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {clientesDisponiveis.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', p: 3 }}>
                    Todos os clientes ja foram selecionados.
                  </Typography>
                ) : clientesDisponiveisFiltrados.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', p: 3 }}>
                    Nenhum cliente disponivel encontrado com "{buscaDisponiveis}".
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {clientesDisponiveisFiltrados.map((cliente, index, array) => (
                      <Box key={cliente.IdCliente}>
                        <ListItem
                          secondaryAction={
                            canEdit ? (
                              <IconButton
                                edge="end"
                                size="small"
                                color="primary"
                                title="Adicionar aos selecionados"
                                onClick={() => onAddCliente(cliente.IdCliente)}
                              >
                                <AddCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            ) : null
                          }
                          sx={{
                            py: 0.75,
                            px: 2,
                            '&:hover': { bgcolor: 'action.hover' },
                            cursor: canEdit ? 'pointer' : 'default'
                          }}
                          onClick={canEdit ? () => onAddCliente(cliente.IdCliente) : undefined}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {cliente.NomeFantasia || cliente.RazaoSocial}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Login: <strong>{cliente.LoginCliente}</strong> | CNPJ: {cliente.CNPJ}
                                {cliente.RazaoSocial && cliente.NomeFantasia ? ` | ${cliente.RazaoSocial}` : ''}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < array.length - 1 && <Divider component="li" />}
                      </Box>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Coluna 2: Clientes Selecionados */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                height: 380,
                overflow: 'hidden'
              }}
            >
              {/* Cabecalho Selecionados */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Clientes Selecionados
                  </Typography>
                  <Chip
                    size="small"
                    color={clientesSelecionados.length > 0 ? 'primary' : 'default'}
                    label={`${clientesSelecionados.length}`}
                    sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
                  />
                </Box>
                {clientesSelecionados.length > 0 && canEdit && (
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    sx={{ py: 0, minHeight: 0, textTransform: 'none', fontSize: '0.75rem' }}
                    onClick={onLimparTodos}
                  >
                    Limpar todos
                  </Button>
                )}
              </Box>

              {/* Barra de Pesquisa dos Selecionados */}
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Pesquisar nos selecionados..."
                  value={buscaSelecionados}
                  onChange={(e) => onBuscaSelecionadosChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: buscaSelecionados ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => onBuscaSelecionadosChange('')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Box>

              {/* Lista de Clientes Selecionados */}
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {clientesSelecionados.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', p: 3 }}>
                    Nenhum cliente selecionado. Clique nos clientes da lista ao lado para adicionar.
                  </Typography>
                ) : clientesSelecionadosFiltrados.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', p: 3 }}>
                    Nenhum cliente selecionado encontrado com "{buscaSelecionados}".
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {clientesSelecionadosFiltrados.map((cliente, index, array) => (
                      <Box key={cliente.IdCliente}>
                        <ListItem
                          secondaryAction={
                            canEdit ? (
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                title="Remover cliente"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onRemoveCliente(cliente.IdCliente)
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            ) : null
                          }
                          sx={{
                            py: 0.75,
                            px: 2,
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {cliente.NomeFantasia || cliente.RazaoSocial}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Login: <strong>{cliente.LoginCliente}</strong> | CNPJ: {cliente.CNPJ}
                                {cliente.RazaoSocial && cliente.NomeFantasia ? ` | ${cliente.RazaoSocial}` : ''}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < array.length - 1 && <Divider component="li" />}
                      </Box>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </Grid>

        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}
